import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { MagicWand, CameraPlus, ArrowLeft, CheckCircle, Warning, Trash, FacebookLogo } from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";
import { geocodeAddress } from "../lib/geocode";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";

function toMinutes(hhmm) {
  const [h, m] = (hhmm || "0:0").split(":").map(Number);
  return h * 60 + m;
}

export default function ScheduleImport() {
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [truckName, setTruckName] = useState("");
  const [publishing, setPublishing] = useState(false);
  const [usage, setUsage] = useState(null); // {is_pro, imports_left, limit}
  const [limitHit, setLimitHit] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/schedule-import/usage").then(({ data }) => {
      setUsage(data);
      if (data && !data.is_pro && data.imports_left === 0) setLimitHit(true);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/trucks/mine").then(({ data }) => {
      const name = (data || [])[0]?.truck_name;
      if (name) setTruckName((n) => n || name);
    }).catch(() => {});
  }, []);

  const [fbBusy, setFbBusy] = useState(false);

  const pullFacebook = async () => {
    setFbBusy(true);
    try {
      const { data } = await api.get("/integrations/facebook/pull-posts");
      const texts = (data.posts || []).map((p) => p.message).filter(Boolean);
      if (!texts.length) { toast.info("No recent text posts found on your page."); return; }
      setText(texts.join("\n\n---\n\n"));
      toast.success(`Pulled ${texts.length} recent post${texts.length > 1 ? "s" : ""} — now tap "Draft my stops".`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't pull Facebook posts — connect your page on the dashboard first.");
    } finally {
      setFbBusy(false);
    }
  };

  const parse = async () => {
    if (!text.trim() && !file) {
      toast.error("Paste your schedule or add a photo first.");
      return;
    }
    setParsing(true);
    try {
      const form = new FormData();
      form.append("text", text);
      if (file) form.append("image", file);
      const { data } = await api.post("/schedule-import/parse", form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
      });
      const rows = (data.drafts || []).map((d) => ({ ...d, selected: d.status === "ready" }));
      setDrafts(rows);
      if (data.imports_left != null) {
        setUsage((u) => ({ ...(u || {}), is_pro: data.is_pro, imports_left: data.imports_left }));
        if (data.imports_left === 0) setLimitHit(true);
      }
      if (!rows.length) toast.info("Couldn't find any stops in that — try adding dates and times.");
      else toast.success(`Found ${rows.length} stop${rows.length > 1 ? "s" : ""} — review below.`);
    } catch (err) {
      if (err.response?.status === 402) setLimitHit(true);
      toast.error(formatApiError(err.response?.data?.detail) || "Parse failed — try again.");
    } finally {
      setParsing(false);
    }
  };

  const update = (id, patch) =>
    setDrafts((prev) => prev.map((d) => (d.draft_id === id ? { ...d, ...patch } : d)));

  const regeocode = async (d) => {
    try {
      const r = await geocodeAddress(d.address);
      if (r?.lat != null && r.is_full_address !== false) {
        update(d.draft_id, {
          lat: r.lat, lng: r.lng,
          address: r.display_name || d.address,
          status: "ready", selected: true,
        });
        toast.success("Address found!");
      } else toast.error("Still can't find that address — add street + ZIP.");
    } catch {
      toast.error("Still can't find that address — add street + ZIP.");
    }
  };

  const publish = async () => {
    const chosen = drafts.filter((d) => d.selected && d.status === "ready");
    if (!chosen.length) { toast.error("Select at least one ready stop."); return; }
    if (!truckName.trim()) { toast.error("Enter your truck name."); return; }
    setPublishing(true);
    let ok = 0, failed = 0;
    for (const d of chosen) {
      const endT = d.end_time || `${String(Math.min(23, Math.floor(toMinutes(d.start_time) / 60) + 3)).padStart(2, "0")}:${d.start_time.slice(3)}`;
      try {
        await api.post("/trucks", {
          truck_name: truckName.trim(),
          event_name: d.event_name || "",
          address: d.address,
          lat: d.lat, lng: d.lng,
          starts_at: `${d.date}T${d.start_time}:00`,
          ends_at: `${d.date}T${endT}:00`,
          menu_text: d.notes || "",
        });
        ok += 1;
        setDrafts((prev) => prev.filter((x) => x.draft_id !== d.draft_id));
      } catch (err) {
        failed += 1;
        update(d.draft_id, { error: formatApiError(err.response?.data?.detail) || "Failed" });
      }
    }
    setPublishing(false);
    if (ok) toast.success(`${ok} stop${ok > 1 ? "s" : ""} published to the map! 🎉`);
    if (failed) toast.error(`${failed} couldn't be published — see the cards below.`);
  };

  const readyCount = drafts.filter((d) => d.selected && d.status === "ready").length;

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-6" data-testid="schedule-import-page">
      <div className="flex items-center gap-3">
        <Link to="/dashboard" className="text-muted-foreground hover:text-foreground" data-testid="import-back-link">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2">
            <MagicWand weight="duotone" className="h-6 w-6 text-primary" /> Magic Schedule Import
          </h1>
          <p className="text-sm text-muted-foreground">
            Paste your schedule from anywhere — Notes app, a text, whatever — or snap a photo of it.
            We'll draft the stops; nothing posts until you confirm.
          </p>
        </div>
      </div>

      <Card>
        <CardContent className="pt-5 space-y-3">
          {usage && !usage.is_pro && (
            <div className="flex items-center justify-between text-xs" data-testid="import-usage-chip">
              <span className={`font-semibold ${usage.imports_left === 0 ? "text-amber-600" : "text-muted-foreground"}`}>
                {usage.imports_left} of {usage.limit} free imports left this month
              </span>
              <Link to="/pricing" className="text-primary font-semibold hover:underline">
                Go Pro for unlimited →
              </Link>
            </div>
          )}
          {limitHit && (
            <div className="rounded-lg border border-amber-400/60 bg-amber-50 dark:bg-amber-950/30 px-4 py-3 text-sm" data-testid="import-limit-banner">
              You've used your 3 free imports this month.{" "}
              <Link to="/pricing" className="font-semibold text-primary underline">Upgrade to Pro</Link>{" "}
              for unlimited Magic Imports — or they reset on the 1st.
            </div>
          )}
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={6}
            placeholder={"Example:\nWed – Central Market 11-2\nFri – 4724 Jefferson Ave Newport News 4-8\nSat – Oyster Point farmers mkt 9-1"}
            data-testid="import-text-input"
          />
          <div className="flex flex-wrap items-center gap-3">
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="import-file-input" />
            <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="import-photo-btn">
              <CameraPlus className="h-4 w-4 mr-1.5" /> {file ? file.name.slice(0, 24) : "Add a photo"}
            </Button>
            <Button variant="outline" size="sm" onClick={pullFacebook} disabled={fbBusy} data-testid="import-facebook-btn">
              <FacebookLogo weight="fill" className="h-4 w-4 mr-1.5 text-[#1877F2]" />
              {fbBusy ? "Pulling…" : "Pull from Facebook"}
            </Button>
            {file && (
              <button className="text-xs text-muted-foreground underline" onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ""; }} data-testid="import-remove-photo">
                remove
              </button>
            )}
            <Button onClick={parse} disabled={parsing || limitHit} className="ml-auto" data-testid="import-parse-btn">
              <MagicWand className="h-4 w-4 mr-1.5" /> {parsing ? "Reading your schedule…" : "Draft my stops"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {drafts.length > 0 && (
        <div className="space-y-3" data-testid="import-drafts-list">
          <div className="flex items-center gap-3">
            <Input value={truckName} onChange={(e) => setTruckName(e.target.value)} placeholder="Truck name"
              className="max-w-[220px]" data-testid="import-truck-name" />
            <Button onClick={publish} disabled={publishing || readyCount === 0} className="ml-auto" data-testid="import-publish-btn">
              {publishing ? "Publishing…" : `Publish ${readyCount} stop${readyCount === 1 ? "" : "s"}`}
            </Button>
          </div>
          {drafts.map((d) => (
            <Card key={d.draft_id} className={d.status !== "ready" ? "border-amber-400/60" : ""} data-testid={`import-draft-${d.draft_id}`}>
              <CardContent className="pt-4 space-y-2">
                <div className="flex items-center gap-2">
                  <input type="checkbox" checked={!!d.selected} disabled={d.status !== "ready"}
                    onChange={(e) => update(d.draft_id, { selected: e.target.checked })}
                    className="h-4 w-4 accent-primary" data-testid={`draft-select-${d.draft_id}`} />
                  <Input value={d.event_name || ""} placeholder="Event name (optional)"
                    onChange={(e) => update(d.draft_id, { event_name: e.target.value })}
                    className="h-8 text-sm font-semibold" data-testid={`draft-name-${d.draft_id}`} />
                  {d.status === "ready"
                    ? <CheckCircle weight="fill" className="h-5 w-5 text-emerald-500 flex-none" />
                    : <Warning weight="fill" className="h-5 w-5 text-amber-500 flex-none" />}
                  <button onClick={() => setDrafts((p) => p.filter((x) => x.draft_id !== d.draft_id))}
                    className="text-muted-foreground hover:text-destructive" data-testid={`draft-remove-${d.draft_id}`}>
                    <Trash className="h-4 w-4" />
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Input type="date" value={d.date} onChange={(e) => update(d.draft_id, { date: e.target.value })}
                    className="h-8 text-sm w-[150px]" data-testid={`draft-date-${d.draft_id}`} />
                  <Input type="time" value={d.start_time} onChange={(e) => update(d.draft_id, { start_time: e.target.value })}
                    className="h-8 text-sm w-[110px]" data-testid={`draft-start-${d.draft_id}`} />
                  <Input type="time" value={d.end_time} onChange={(e) => update(d.draft_id, { end_time: e.target.value })}
                    className="h-8 text-sm w-[110px]" data-testid={`draft-end-${d.draft_id}`} />
                </div>
                <div className="flex gap-2">
                  <Input value={d.address} onChange={(e) => update(d.draft_id, { address: e.target.value, status: "needs_address" })}
                    className="h-8 text-sm flex-1" data-testid={`draft-address-${d.draft_id}`} />
                  {d.status !== "ready" && (
                    <Button variant="outline" size="sm" className="h-8" onClick={() => regeocode(d)} data-testid={`draft-geocode-${d.draft_id}`}>
                      Find address
                    </Button>
                  )}
                </div>
                {d.status !== "ready" && !d.error && (
                  <p className="text-xs text-amber-600">We couldn't pin this one — add a street address + ZIP, then tap "Find address".</p>
                )}
                {d.error && <p className="text-xs text-destructive">{d.error}</p>}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
